import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormErrorsSchema } from "../../common/types";
import { mergeErrors } from "../../common/utils";
import notifications from "../../common/services/notifications";
import { fetchResults, saveResults } from "../../api/results";

interface UseResultFormOptions {
  loadExisting?: boolean;
}

export const useResultForm = (serialNumber?: string, options: UseResultFormOptions = {}) => {
  const { loadExisting = false } = options;
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});
  const [loading, setLoading] = useState<boolean>(loadExisting);
  const navigate = useNavigate();

  useEffect(() => {
    if (loadExisting && serialNumber) {
      fetchResults(serialNumber).then(async (response) => {
        if (response?.ok) {
          const responseBody = await response.json();
          if (responseBody?.data?.current?.result) {
            setSelectedOption(responseBody.data.current.result);
            setLoading(false);
          } else {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      });
    }
  }, [loadExisting, serialNumber]);

  const submit = async () => {
    setLoading(true);
    const response = await saveResults(String(serialNumber), selectedOption);

    if (!response?.ok) {
      if (response?.status === 422) {
        const errorData = await response.json();
        setFormErrors(mergeErrors(errorData.errors));
      } else {
        notifications.error({
          message:
            "An unexpected error has occurred, please try again or contact our support team at support@jackathome.com for further assistance.",
        });
        navigate(`/search`);
      }
    }

    if (response?.ok) {
      const responseBody = await response.json();
      if (responseBody?.data?.current) {
        notifications.success({
          message: "The result was successfully saved.",
        });
        navigate(`/search`);
      } else {
        setFormErrors({
          result: [
            "An unexpected error has occurred, please try again or contact our support team at support@jackathome.com for further assistance.",
          ],
        });
      }
    }

    setLoading(false);
  };

  const handleOnChange = async () => setFormErrors({});

  return {
    selectedOption,
    setSelectedOption,
    formErrors,
    loading,
    submit,
    handleOnChange,
  };
};

